/**
 * Weigh-ins and the trailing 7-day mean, section 5.
 *
 * Section 3, non-negotiable: "Checkpoint weight criteria read a trailing 7-day mean,
 * never a single weigh-in and never a best-ever figure."
 *
 * MIN_WEIGH_INS_FOR_MEAN exists because section 5's pseudocode alone does not deliver
 * that. Literally implemented, a week containing ONE dehydrated morning reading returns
 * that reading as the week's "7-day mean", and bestMeanUpTo then keeps it forever -
 * precisely the failure section 5 says bestMeanUpTo is designed to prevent ("without a
 * single dehydrated morning ever being able to clear it"). Acceptance tests 11 and 12
 * contrast one weigh-in against four, and gate 1 asks for "3 or more weigh-ins" in a
 * week, so three is the plan's own standard for a week's data being real.
 *
 * This is a deliberate, flagged decision, not a silent correction. Changing the constant
 * to 1 restores the literal pseudocode.
 */
import type { PlanState } from './types.ts';
import { dateForDayIndex } from './dates.ts';

export const MIN_WEIGH_INS_FOR_MEAN = 3;

export interface WeighIn { date: string; kg: number; bf?: number }

export function weighIns(state: PlanState): WeighIn[] {
  return state.entries('weight:')
    .map(([k, v]) => {
      const val = v as { kg?: number; bf?: number };
      return { date: k.slice('weight:'.length), kg: Number(val?.kg), bf: val?.bf };
    })
    .filter((w) => Number.isFinite(w.kg))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

export function waistEntries(state: PlanState): Array<{ date: string; cm: number }> {
  return state.entries('waist:')
    .map(([k, v]) => ({ date: k.slice('waist:'.length), cm: Number((v as { cm?: number })?.cm) }))
    .filter((w) => Number.isFinite(w.cm))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * meanWeightForWeek: the mean of weigh-ins in the seven calendar days ending on the
 * last day of `week`. Returns null when there are none, or too few to be a real mean.
 */
export function meanWeightForWeek(
  state: PlanState, startDate: string, week: number,
  minEntries = MIN_WEIGH_INS_FOR_MEAN,
): number | null {
  const endIso = dateForDayIndex(startDate, week * 7 - 1);
  const startIso = dateForDayIndex(startDate, week * 7 - 7);
  const inWindow = weighIns(state).filter((w) => w.date >= startIso && w.date <= endIso);
  if (inWindow.length < Math.max(1, minEntries)) return null;
  return inWindow.reduce((s, w) => s + w.kg, 0) / inWindow.length;
}

/** The lowest genuine weekly mean achieved up to and including `week`. Null if none. */
export function bestMeanUpTo(
  state: PlanState, startDate: string, week: number,
  minEntries = MIN_WEIGH_INS_FOR_MEAN,
): number | null {
  let best: number | null = null;
  for (let w = 1; w <= week; w++) {
    const m = meanWeightForWeek(state, startDate, w, minEntries);
    if (m !== null && (best === null || m < best)) best = m;
  }
  return best;
}

/** How many weigh-ins fall in a given week's window. Used by the UI to explain a null. */
export function weighInCountForWeek(state: PlanState, startDate: string, week: number): number {
  const endIso = dateForDayIndex(startDate, week * 7 - 1);
  const startIso = dateForDayIndex(startDate, week * 7 - 7);
  return weighIns(state).filter((w) => w.date >= startIso && w.date <= endIso).length;
}
